/**
 * sl-lkdb.3.2: Hook to detect PITR recovery and trigger IDB clear + re-sync.
 *
 * Checks the server's recoveryVersion against localStorage on mount.
 * Also subscribes to the WebSocket recoveryCompleted$ event for real-time
 * notification when an admin triggers recovery while the user is online.
 *
 * When a version mismatch is detected for an existing local cache:
 * 1. Shows a blocking dialog (via returned state)
 * 2. Deletes all workspace IDB databases (stale Y.js data)
 * 3. Reloads the page to trigger fresh sync from server
 */
import { getWorkspaceInfoQuery } from '@affine/graphql';
import { isDebugVerbosityAtLeast } from '@affine/nbstore';
import { useServiceOptional } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useBackendAvailable } from '../../../components/hooks/use-backend-availability';
import { isBackendUnavailableError } from '../../../utils/backend-availability';
import { WorkspaceServerService } from '../../cloud';
import type { Workspace } from '../entities/workspace';
import {
  RECOVERY_VERSION_KEY,
  shouldRefreshLocalWorkspaceCache,
} from './workspace-recovery-version';

/**
 * List all IndexedDB databases belonging to a specific workspace.
 * Same logic as in use-role-aware-root-doc-ready.ts — shared utility.
 */
async function listWorkspaceIDBDatabases(
  workspaceId: string
): Promise<IDBDatabaseInfo[]> {
  if (!window.indexedDB?.databases) {
    return [];
  }

  const databases = await window.indexedDB.databases();
  return databases.filter(db => db.name?.includes(workspaceId));
}

/**
 * Delete all IndexedDB databases belonging to a specific workspace.
 */
async function deleteWorkspaceIDBDatabases(workspaceId: string): Promise<void> {
  try {
    const workspaceDbs = await listWorkspaceIDBDatabases(workspaceId);
    for (const db of workspaceDbs) {
      if (db.name) {
        console.log(`[useWorkspaceRecovery] Deleting IDB database: ${db.name}`);
        window.indexedDB.deleteDatabase(db.name);
      }
    }
    console.log(
      `[useWorkspaceRecovery] Deleted ${workspaceDbs.length} IDB databases for workspace ${workspaceId}`
    );
  } catch (error) {
    console.error(
      '[useWorkspaceRecovery] Failed to delete workspace IDB databases:',
      error
    );
  }
}

export interface WorkspaceRecoveryState {
  /** True when recovery is in progress (dialog should be shown) */
  isRecovering: boolean;
  /** Current recovery phase for localized user-facing copy */
  phase: WorkspaceRecoveryPhase;
  /** Determinate stage progress, 0..1 */
  progressPercent: number | null;
  /** Error message if recovery failed */
  error: string | null;
  /** Retry the recovery process */
  retry: () => void;
}

export type WorkspaceRecoveryPhase =
  | 'idle'
  | 'clearingLocalData'
  | 'syncingFreshData'
  | 'reloading';

export function useWorkspaceRecovery(
  workspace: Workspace | null
): WorkspaceRecoveryState {
  const [isRecovering, setIsRecovering] = useState(false);
  const [phase, setPhase] = useState<WorkspaceRecoveryPhase>('idle');
  const [progressPercent, setProgressPercent] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasChecked = useRef(false);
  const serverService = useServiceOptional(WorkspaceServerService);
  const backendAvailable = useBackendAvailable();

  const isLocal = !workspace || workspace.flavour === 'local';
  const workspaceId = workspace?.id ?? '';

  const performRecovery = useCallback(
    async (newVersion: number) => {
      if (!workspaceId) return;
      setIsRecovering(true);
      setError(null);
      setProgressPercent(0.2);
      setPhase('clearingLocalData');

      try {
        // Step 1: Delete all IDB databases for this workspace
        await deleteWorkspaceIDBDatabases(workspaceId);

        // Step 2: Update localStorage with new version
        setProgressPercent(0.65);
        setPhase('syncingFreshData');
        localStorage.setItem(
          RECOVERY_VERSION_KEY(workspaceId),
          String(newVersion)
        );

        // Step 3: Reload to trigger fresh sync from server
        // Short delay so user sees progress message
        await new Promise(resolve => setTimeout(resolve, 500));
        setProgressPercent(0.9);
        setPhase('reloading');
        window.location.reload();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Unknown error during recovery';
        console.error('[useWorkspaceRecovery] Recovery failed:', err);
        setError(message);
        setPhase('idle');
        setProgressPercent(null);
        setIsRecovering(false);
      }
    },
    [workspaceId]
  );

  // Check recovery version on mount (via GraphQL)
  useEffect(() => {
    if (
      isLocal ||
      hasChecked.current ||
      !backendAvailable ||
      !serverService?.server ||
      !workspaceId
    ) {
      return;
    }
    hasChecked.current = true;

    void (async () => {
      try {
        const result = await serverService.server!.gql({
          query: getWorkspaceInfoQuery,
          variables: { workspaceId },
        });

        const serverVersion = result.workspace.recoveryVersion;
        const localVersionStr = localStorage.getItem(
          RECOVERY_VERSION_KEY(workspaceId)
        );
        const localVersion = localVersionStr
          ? Number.parseInt(localVersionStr, 10)
          : null;
        const hasLocalWorkspaceDatabases =
          (await listWorkspaceIDBDatabases(workspaceId)).length > 0;

        if (isDebugVerbosityAtLeast(3)) {
          console.log(
            `[useWorkspaceRecovery] Version check: server=${serverVersion}, local=${localVersion ?? 'missing'}, localDbs=${hasLocalWorkspaceDatabases}`
          );
        }

        if (
          shouldRefreshLocalWorkspaceCache({
            serverVersion,
            localVersionStr,
            hasLocalWorkspaceDatabases,
          })
        ) {
          console.warn(
            `[useWorkspaceRecovery] Recovery detected! server=${serverVersion} > local=${localVersion ?? 'missing'} with local workspace data`
          );
          void performRecovery(serverVersion);
        } else {
          // Ensure localStorage is in sync. First visits without local workspace
          // data are not recovery events; they just pin the current server marker.
          if (localVersionStr !== String(serverVersion)) {
            localStorage.setItem(
              RECOVERY_VERSION_KEY(workspaceId),
              String(serverVersion)
            );
          }
        }
      } catch (err) {
        // Non-critical — if the GQL call fails, we just skip the check.
        // The WebSocket subscription below will catch real-time events.
        hasChecked.current = false;
        if (!isBackendUnavailableError(err)) {
          console.warn(
            '[useWorkspaceRecovery] Failed to fetch recovery version:',
            err
          );
        }
      }
    })();
  }, [backendAvailable, isLocal, serverService, workspaceId, performRecovery]);

  // Subscribe to real-time WebSocket recoveryCompleted$ events
  useEffect(() => {
    const recoveryCompleted$ = workspace?.engine.recoveryCompleted$;
    if (!recoveryCompleted$ || isLocal) {
      return;
    }

    const subscription = recoveryCompleted$.subscribe(event => {
      console.warn(
        `[useWorkspaceRecovery] WebSocket recovery event received:`,
        event
      );
      void performRecovery(event.newRecoveryVersion);
    });

    return () => subscription.unsubscribe();
  }, [workspace, isLocal, performRecovery]);

  const retry = useCallback(() => {
    if (!workspaceId) return;
    setError(null);
    const localVersionStr = localStorage.getItem(
      RECOVERY_VERSION_KEY(workspaceId)
    );
    const version = localVersionStr ? parseInt(localVersionStr, 10) + 1 : 1;
    void performRecovery(version);
  }, [workspaceId, performRecovery]);

  return { isRecovering, phase, progressPercent, error, retry };
}
